import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Button, Tag, Tile } from "@carbon/react";
import { Chat as ChatIcon } from "@carbon/icons-react";
import { GoBackArrow, Pill } from "../../components";
import SecondRoundTag from "../../components/UI/SecondRoundTag/SecondRoundTag";
import BidHeader from "./BidHeader";
import { OrderResponse } from "../../types";
import { formatBiddingDateTime, labelType } from "../../utils";
import "./bid.scss";

export type SellerOfferHeaderProps = {
  order?: OrderResponse;
  orderName?: string;
  selected: boolean;
  chatCount: number;
  onOpenChat: () => void;
  /** Whether to show the "Will not quote" (decline the whole order) action beside Chat. */
  canDecline?: boolean;
  isDeclining?: boolean;
  onDecline?: () => void;
};

/**
 * The seller offer view's top chrome (Task 6) — the "split into header/…" piece shared by
 * `SellerOffer.tsx` (single) and `JointSellerOffer.tsx` (joint): a back arrow + chat trigger
 * toolbar and the order name / date-range / status-pill card. Both views rendered this
 * identically, so it lives here once.
 */
const SellerOfferHeader = ({
  order,
  orderName,
  selected,
  chatCount,
  onOpenChat,
  canDecline = false,
  isDeclining = false,
  onDecline,
}: SellerOfferHeaderProps) => {
  const { t } = useTranslation();

  return (
    <Fragment>
      <div className="seller-offer__toolbar">
        <GoBackArrow isOffer />
        <div className="seller-offer__toolbar-actions">
          {canDecline && onDecline && (
            <Button
              kind="danger--tertiary"
              size="sm"
              disabled={isDeclining}
              onClick={onDecline}
            >
              {t("offer.declineOffer.button")}
            </Button>
          )}
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={ChatIcon}
            onClick={onOpenChat}
          >
            {t("chat.title")}
            {chatCount > 0 && (
              <span className="bid-toolbar__badge">{chatCount}</span>
            )}
          </Button>
        </div>
      </div>

      <Tile className="seller-offer__header-card">
        <BidHeader order={order} orderName={orderName} selected={selected} />
        <div className="seller-offer__dates">
          {t("tenders.tenderSummary.start")}{" "}
          {formatBiddingDateTime(order?.opensAt, order?.startDate)}
          {" - "}
          {t("tenders.tenderSummary.end")} {formatBiddingDateTime(order?.closesAt, order?.finishDate)}
        </div>
        <div className="seller-offer__status">
          <Pill order={order} selected={selected} isSeller />
          {order?.type && (
            <Tag type="cool-gray" size="sm">
              {labelType(order.type)}
            </Tag>
          )}
          <SecondRoundTag round={order?.round} />
        </div>
      </Tile>
    </Fragment>
  );
};

export default SellerOfferHeader;
